import React, { useState, useEffect } from 'react';
import { getUserValuations } from '../../services/api';
import './AdvisorDashboard.css';

const AdvisorDashboard = ({ userId, onSelectValuation, onCreateNew }) => {
  const [valuations, setValuations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadValuations = async () => {
      try {
        setLoading(true);
        const data = await getUserValuations(userId);
        setValuations(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load valuations');
        setLoading(false);
      }
    };

    if (userId) {
      loadValuations();
    }
  }, [userId]);

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (valuation) => {
    // Determine status based on valuation data
    if (!valuation.recommendedValuation) {
      return { label: 'Draft', className: 'draft' };
    }
    if (valuation.completedImprovements?.length > 0) {
      return { label: 'In Progress', className: 'in-progress' };
    }
    return { label: 'Complete', className: 'complete' };
  };

  const filteredValuations = valuations.filter(v => {
    // Apply status filter
    if (filter !== 'all') {
      const status = getStatusBadge(v);
      if (filter === 'draft' && status.className !== 'draft') return false;
      if (filter === 'in-progress' && status.className !== 'in-progress') return false;
      if (filter === 'complete' && status.className !== 'complete') return false;
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const companyName = v.inputData?.companyName?.toLowerCase() || '';
      return companyName.includes(search);
    }

    return true;
  });

  if (loading) {
    return (
      <div className="advisor-dashboard loading">
        <div className="spinner"></div>
        <p>Loading engagements...</p>
      </div>
    );
  }

  return (
    <div className="advisor-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Client Engagements</h1>
          <p className="subtitle">Manage value acceleration assessments</p>
        </div>
        <button className="btn btn-primary" onClick={onCreateNew}>
          + New Assessment
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      <div className="dashboard-controls">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search by company name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-tabs">
          <button
            className={`tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({valuations.length})
          </button>
          <button
            className={`tab ${filter === 'draft' ? 'active' : ''}`}
            onClick={() => setFilter('draft')}
          >
            Draft
          </button>
          <button
            className={`tab ${filter === 'in-progress' ? 'active' : ''}`}
            onClick={() => setFilter('in-progress')}
          >
            In Progress
          </button>
          <button
            className={`tab ${filter === 'complete' ? 'active' : ''}`}
            onClick={() => setFilter('complete')}
          >
            Complete
          </button>
        </div>
      </div>

      {filteredValuations.length === 0 ? (
        <div className="empty-state">
          {valuations.length === 0 ? (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3>No Assessments Yet</h3>
              <p>Create your first value acceleration assessment to get started.</p>
              <button className="btn btn-primary" onClick={onCreateNew}>
                Create Assessment
              </button>
            </>
          ) : (
            <>
              <h3>No Matching Results</h3>
              <p>Try adjusting your search or filter criteria.</p>
            </>
          )}
        </div>
      ) : (
        <div className="valuations-table">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Industry</th>
                <th>Current Value</th>
                <th>Risk Score</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredValuations.map((valuation) => {
                const status = getStatusBadge(valuation);
                return (
                  <tr key={valuation.id}>
                    <td className="company-name">
                      {valuation.inputData?.companyName || 'Unnamed'}
                    </td>
                    <td>{valuation.inputData?.industry || '-'}</td>
                    <td className="value">
                      {formatCurrency(valuation.recommendedValuation)}
                    </td>
                    <td>
                      {valuation.riskScore ? (
                        <span className={`risk-badge ${valuation.riskCategory?.toLowerCase()}`}>
                          {valuation.riskScore}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`status-badge ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>{formatDate(valuation.updatedAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          onClick={() => onSelectValuation(valuation.id)}
                          title="View Details"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => onSelectValuation(valuation.id, 'edit')}
                          title="Edit"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-card">
          <span className="stat-value">{valuations.length}</span>
          <span className="stat-label">Total Assessments</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {formatCurrency(
              valuations.reduce((sum, v) => sum + (v.recommendedValuation || 0), 0) / valuations.length || 0
            )}
          </span>
          <span className="stat-label">Avg. Valuation</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {valuations.filter(v => getStatusBadge(v).className === 'complete').length}
          </span>
          <span className="stat-label">Completed</span>
        </div>
      </div>
    </div>
  );
};

export default AdvisorDashboard;
